import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { PopupsEducativosService, PopupEducativo, PopupsConfig } from '../../services/popupsEducativosService';

const PopupsEducativosRotativos: React.FC = () => {
  const { user } = useAuth();
  const [popups, setPopups] = useState<PopupEducativo[]>([]);
  const [config, setConfig] = useState<PopupsConfig | null>(null);
  const [popupAtual, setPopupAtual] = useState<PopupEducativo | null>(null);
  const [visivel, setVisivel] = useState(false);
  const indiceRef = useRef(0);
  const intervaloRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const popupsRef = useRef<PopupEducativo[]>([]);

  const mostrarPopup = useCallback(() => {
    const lista = popupsRef.current;
    if (lista.length === 0) return;
    setPopupAtual(lista[indiceRef.current]);
    setVisivel(true);
  }, []);

  // Carregar config e popups ao montar
  useEffect(() => {
    if (!user) return;

    const carregarDados = async () => {
      try {
        const [cfg, popupsAtivos] = await Promise.all([
          PopupsEducativosService.buscarConfig(),
          PopupsEducativosService.buscarAtivos()
        ]);

        setConfig(cfg);
        setPopups(popupsAtivos);
        popupsRef.current = popupsAtivos;

        if (!cfg.ativo || popupsAtivos.length === 0) return;

        const perfilUser = (user as any)?.perfil || '';
        const perfilId = (user as any)?.perfil_id || '';
        const perfisPermitidos = cfg.perfis_permitidos || [];
        const temPermissaoPerfil = perfisPermitidos.length === 0 ||
          perfisPermitidos.includes(perfilUser) ||
          perfisPermitidos.includes(perfilId) ||
          perfilUser === 'admin';

        if (!temPermissaoPerfil) return;

        // Primeiro popup após 10 segundos
        setTimeout(() => {
          indiceRef.current = 0;
          mostrarPopup();
        }, 10000);
      } catch (error) {
        console.error('Erro ao carregar popups educativos:', error);
      }
    };

    carregarDados();
  }, [user, mostrarPopup]);

  // Intervalo: a cada X minutos, mostra o próximo popup (mesmo se o anterior foi fechado)
  useEffect(() => {
    if (!config || !config.ativo || popups.length === 0) return;

    const intervaloMs = (config.intervalo_minutos || 5) * 60 * 1000;

    intervaloRef.current = setInterval(() => {
      indiceRef.current = (indiceRef.current + 1) % popupsRef.current.length;
      mostrarPopup();
    }, intervaloMs);

    return () => {
      if (intervaloRef.current) clearInterval(intervaloRef.current);
    };
  }, [config, popups, mostrarPopup]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (intervaloRef.current) clearInterval(intervaloRef.current);
    };
  }, []);

  const handleFechar = () => {
    setVisivel(false);
  };

  const handleProximo = () => {
    if (popupsRef.current.length === 0) return;
    indiceRef.current = (indiceRef.current + 1) % popupsRef.current.length;
    setPopupAtual(popupsRef.current[indiceRef.current]);
  };

  const handleAnterior = () => {
    if (popupsRef.current.length === 0) return;
    indiceRef.current = indiceRef.current === 0 ? popupsRef.current.length - 1 : indiceRef.current - 1;
    setPopupAtual(popupsRef.current[indiceRef.current]);
  };

  if (!visivel || !popupAtual) return null;

  return (
    <div
      role="dialog"
      aria-modal="false"
      className="fixed bottom-6 right-6 z-50 max-w-sm animate-slide-in"
      style={{
        animation: 'slideIn 0.3s ease-out',
      }}
    >
      <style>{`
        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
      <div className="bg-white rounded-xl shadow-2xl border border-blue-100 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-500 px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-white text-xs font-semibold uppercase tracking-wide">Dica do Sistema</span>
          </div>
          <button
            onClick={handleFechar}
            className="text-white hover:bg-blue-700 rounded-full p-1 transition-colors"
            aria-label="Fechar"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Conteúdo */}
        <div className="px-5 py-4">
          <h3 className="text-base font-bold text-gray-900 mb-2">{popupAtual.titulo}</h3>
          <p className="text-sm text-gray-600 leading-relaxed">{popupAtual.conteudo}</p>
        </div>

        {/* Footer com navegação */}
        <div className="px-5 py-3 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
          <div className="flex gap-1">
            {popups.map((_, idx) => (
              <span
                key={idx}
                className={`h-1.5 rounded-full transition-all ${
                  idx === indiceRef.current ? 'w-4 bg-blue-600' : 'w-1.5 bg-gray-300'
                }`}
              />
            ))}
          </div>
          <div className="flex gap-2">
            {popups.length > 1 && (
              <>
                <button
                  onClick={handleAnterior}
                  className="text-gray-400 hover:text-gray-600 p-1"
                  aria-label="Anterior"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  onClick={handleProximo}
                  className="text-blue-600 hover:text-blue-800 p-1"
                  aria-label="Próximo"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PopupsEducativosRotativos;
